import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { ToggleService } from '../../../shared/services/toggle/toggle.service';
import { Observable, of, switchMap, take, combineLatest, filter, map } from 'rxjs';
import { Store } from '@ngrx/store';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { getCurrentUser, getCurrentUserErrorStatus, getCurrentUserWithOpScopesLoadedStatus } from 'Src/ng2/store/selectors/current-user-selectors';
import { SetToggleProperty } from 'Src/ng2/store/actions/toggle-actions';
import { LoadCurrentUserWithOpScopes } from 'Src/ng2/store/actions/current-user-actions';
import { ENTITY_TYPE, TOpEntityType } from 'Src/ng2/shared/typings/types/entity-type.types';
import { ENTITY_MODE, TEntityMode } from 'Src/ng2/shared/typings/types/entity-mode.types';
import { getToggleEntities } from 'Src/ng2/store/selectors/toggles-selectors';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { UrlPathService } from 'Src/ng2/shared/services/url-path-service/url-path.service';

@Injectable({
  providedIn: 'root',
})
export class EntityResourceGuard implements CanActivate {
  constructor (
    private router: Router,
    private toggleService: ToggleService,
    private store: Store<any>,
    private urlPathService: UrlPathService,
  ) {}

  canActivate (snapshot: ActivatedRouteSnapshot): Observable<UrlTree | boolean> {
    return combineLatest([
      this.store.select(getCurrentUser),
      this.store.select(getToggleEntities),
      this.store.select(getCurrentUserWithOpScopesLoadedStatus),
    ]).pipe(
      take(1),
      switchMap(([user, toggles, opScopesLoaded]) => {
        const entityType = UrlNavigationService.getEntityType(snapshot) as TOpEntityType;
        const entityMode = UrlNavigationService.getEntityMode(snapshot);
        const networkId = UrlNavigationService.getNetworkId(snapshot);
        const enabled = this.determinePortalEnabled({ entityType, entityMode, user, toggles });
        const shouldWaitForOpScopes = enabled && !!user && !opScopesLoaded;

        if (shouldWaitForOpScopes) {
          // after op is released to everyone, we can roll the dispatch into the original user load action
          this.store.dispatch(new LoadCurrentUserWithOpScopes());
          return this.waitForOpScopesOrFail$();
        }

        if (entityMode === ENTITY_MODE.SINGLE_ENTITY) {
          // for single entity mode, proceed regardless of toggle state
          // toggle state is used to determine the layout, not the url access
          return of(true);
        }

        // for multi entity mode, only allow access if toggle is enabled
        return of(enabled ? true : this.navigateToLegacyNetworkPortal(entityType, networkId));
      }),
    );
  }

  private determinePortalEnabled (
    opts: {
      entityType: TOpEntityType,
      entityMode: TEntityMode,
      user: IUser,
      toggles: { [key: string]: boolean },
    },
  ): boolean {
    const { entityType, entityMode, user, toggles } = opts;
    // Check if EVALUATION_ONE_PORTAL_ENABLED exists in toggles slice
    if (toggles?.[Toggles.EVALUATION_ONE_PORTAL_ENABLED] !== undefined) {
      return toggles[Toggles.EVALUATION_ONE_PORTAL_ENABLED];
    }

    const enabled = this.toggleService.onePortalEnabled({
      entityType,
      entityMode,
      user,
    });
    this.persistPortalEnabledState(enabled);
    return enabled;
  }

  private persistPortalEnabledState (enabled: boolean): void {
    this.store.dispatch(new SetToggleProperty({
      [Toggles.EVALUATION_ONE_PORTAL_ENABLED]: enabled,
      [Toggles.TOGGLE_ONE_PORTAL]: enabled,
    }));
  }

  private navigateToLegacyNetworkPortal (entityType: TOpEntityType, networkId: string): UrlTree {
    if (!networkId) {
      return this.router.createUrlTree(['/not-found']);
    }

    if (entityType === ENTITY_TYPE.SCHOOL) {
      const url = this.urlPathService.computeDistrictUrlPath(`/network/school/${networkId}`);
      return this.router.createUrlTree([url]);
    }

    if (entityType === ENTITY_TYPE.SHELTER) {
      const url = this.urlPathService.computeDistrictUrlPath(`/network/shelter/${networkId}`);
      return this.router.createUrlTree([url]);
    }

    return this.router.createUrlTree(['/not-found']);
  }

  private waitForOpScopesOrFail$ (): Observable<UrlTree | boolean> {
    return combineLatest([
      this.store.select(getCurrentUser),
      this.store.select(getCurrentUserErrorStatus),
      this.store.select(getCurrentUserWithOpScopesLoadedStatus),
    ]).pipe(
      filter(([updatedUser, error, opScopesLoaded]) => {
        return (!!updatedUser && opScopesLoaded) || !!error;
      }),
      take(1),
      map(([updatedUser, error, opScopesLoaded]) => {
        if (!!error && (!updatedUser || !opScopesLoaded)) {
          return this.router.createUrlTree(['/not-found']);
        }

        return true;
      }),
    );
  }
}
